import './resolutionSelector.less';

import React from 'react';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { Designer } from 'controls/designer/designer';
import { Actions } from 'controls/designer/actionsManager/actions';
import { Action } from 'controls/designer/actionsManager/action';
import { linkModel, MobxManager } from 'framework/mobx-integration';
import { AntSelect } from 'controls/react/ant/ant-select/antSelect';
import { AntInputNumber } from 'controls/react/ant/antInputNumber';
import { DesignerStore } from 'controls/designer/designerStore';

import { i18n } from 'core/localization';

const ow = require('b_').with('designer-preview');

export async function enableFeature(designer: Designer) {
	const store = new ResolutionSelectorStore({
		store: designer.store,
		onChange: (width, height) => {
			const svg = designer.graph.container;
			svg.style.width = width + 'px';
			svg.style.height = height + 'px';

			designer.editorUi.chromelessResize();
		},
	});

	designer.store.am.add(
		new Action({
			id: Actions.SelectContainerSize,
			title: 'Update Resolution',
			toolbarEntryRenderer: () => {
				return <ResolutionSelector key={Actions.SelectContainerSize} store={store} />;
			},
		}),
	);

	const originalDestroy = designer.destroy;
	designer.destroy = function () {
		store?.destroy();
		// @ts-ignore
		originalDestroy.apply(this, arguments);
	};
}

export class Resolution {
	width: number;
	height: number;
}

export const predefinedResolutions = [
	{
		label: 'iPhone 16 Pro',
		resolution: {
			width: 402,
			height: 874,
		},
	},
	{
		label: 'iPhone 16 Pro Max',
		resolution: {
			width: 440,
			height: 956,
		},
	},
	{
		label: 'iPhone 15 Pro',
		resolution: {
			width: 393,
			height: 852,
		},
	},
	{
		label: 'iPhone 15 Pro Max',
		resolution: {
			width: 430,
			height: 932,
		},
	},
	{
		label: 'Samsung Galaxy S24 Ultra',
		resolution: {
			width: 384,
			height: 824,
		},
	},
	{
		label: 'Samsung Galaxy S24+',
		resolution: {
			width: 384,
			height: 832,
		},
	},
	{
		label: 'Samsung Galaxy S24',
		resolution: {
			width: 360,
			height: 780,
		},
	},
	{
		label: 'Google Pixel 9 Pro XL',
		resolution: {
			width: 412,
			height: 915,
		},
	},
	{
		label: 'Google Pixel 9',
		resolution: {
			width: 412,
			height: 915,
		},
	},
	{
		label: 'FullHD',
		resolution: {
			width: 1920,
			height: 1080,
		},
	},
	{
		label: 'Ipad Air',
		resolution: {
			width: 820,
			height: 1180,
		},
	},
	{
		label: 'Galaxy tab S7',
		resolution: {
			width: 800,
			height: 1280,
		},
	},
	{
		label: 'Laptop',
		resolution: {
			width: 1366,
			height: 768,
		},
	},
];

const predefinedResolutionsDataList = [
	{
		label: i18n('Custom'),
		value: -1,
	},
	...predefinedResolutions.map((x, i) => ({
		label: x.label,
		value: i + 1,
	})),
];

export type ResolutionSelectorStoreProps = {
	store: DesignerStore;
	onChange: (width: number, height: number) => void;
	width?: number;
	height?: number;
};

export class ResolutionSelectorStore {
	resolution: Resolution;
	predefinedResolution: number;

	mobx = new MobxManager();

	constructor(public props: ResolutionSelectorStoreProps) {
		this.predefinedResolution = this.getDefaultSelection();
		if (this.predefinedResolution != -1) {
			this.resolution = { ...this.getPredefinedResolution() };
		} else {
			this.resolution = {
				width: this.props.width,
				height: this.props.height,
			};
		}

		makeObservable(this, {
			resolution: observable,
			predefinedResolution: observable,
		});

		this.mobx.reaction(
			() => this.predefinedResolution,
			() => {
				if (this.predefinedResolution == -1) return;

				this.resolution = {
					...this.getPredefinedResolution(),
				};
			},
		);

		this.mobx.reaction(
			() => ({ ...this.resolution }),
			() => {
				if (this.isNewResolutionTheSameAsPredefined()) {
					return;
				}

				this.predefinedResolution = -1;
			},
		);

		this.mobx.reaction(
			() => ({ ...this.resolution, scale: this.props.store?.scale / 100 }),
			(v) => {
				this.props.onChange(Math.round(this.resolution.width * v.scale), Math.round(this.resolution.height * v.scale));
			},
			{
				fireImmediately: true,
			},
		);
	}

	getDefaultSelection() {
		if (this.props.width == null || this.props.height == null) return 6;

		const predefinedResolutionIndex = predefinedResolutions.findIndex(
			(x) => x.resolution.width == this.props.width && x.resolution.height == this.props.height,
		);

		return predefinedResolutionIndex == -1 ? -1 : predefinedResolutionIndex + 1;
	}

	isNewResolutionTheSameAsPredefined() {
		const resolution = this.getPredefinedResolution();
		return resolution != null && resolution.width == resolution.width && this.resolution.height == resolution.height;
	}

	getPredefinedResolution() {
		if (this.predefinedResolution == -1) return null;

		return predefinedResolutions[this.predefinedResolution - 1].resolution;
	}

	updateResolution(width: number, height: number) {
		this.resolution = {
			width,
			height,
		};
	}

	destroy() {
		this.mobx.destroy();
	}
}

export class ResolutionSelectorProps {
	store: ResolutionSelectorStore;
}

const b = require('b_').with('resolution-selector');

export const ResolutionSelector = observer((props: ResolutionSelectorProps) => {
	const store = props.store;

	return (
		<div className={b()}>
			<AntSelect
				className={b('predefined')}
				options={predefinedResolutionsDataList}
				{...linkModel(store, 'predefinedResolution')}
			/>
			<AntInputNumber className={b('width')} {...linkModel(store.resolution, 'width')} />
			x
			<AntInputNumber className={b('height')} {...linkModel(store.resolution, 'height')} />
		</div>
	);
});
